import _ from 'lodash'
import {ReportableError} from '@wix/document-manager-utils'
import {createInnerRef, getIdFromRef} from '../utils/dataUtils'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import {DATA_TYPES, MASTER_PAGE_ID} from '../constants/constants'
import type {KeyframeEffectData, Pointer} from '@wix/document-services-types'
import {DalValue, DmApis, Extension, ExtensionAPI, pointerUtils} from '@wix/document-manager-core'
import {type DataFixerVersioningApi, FixerCategory} from './dataFixerVersioning/dataFixerVersioning'

const EFFECTS = DATA_TYPES.effects
const {getPointer, getInnerPointer} = pointerUtils
const keyframeEffectListPointer = {id: 'KEYFRAME_EFFECT_LIST', type: EFFECTS}

const createExtension = (): Extension => {
    const createExtensionAPI = ({dal, extensionAPI, pointers}: DmApis): ExtensionAPI => {
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        const {dataFixerVersioning} = extensionAPI as DataFixerVersioningApi

        const getKeyframeEffectList = (): DalValue => {
            return dal.get(keyframeEffectListPointer)
        }

        const getAllKeyframeEffectPointers = (): Pointer[] => {
            const idsList = getKeyframeEffectList()?.values ?? []
            return _.map(idsList, id => getPointer(getIdFromRef(id), EFFECTS))
        }

        const addKeyframeEffect = (keyframeEffectPointer: Pointer, keyframeEffectData: KeyframeEffectData): Pointer => {
            dataModel.addItem(keyframeEffectData, EFFECTS, MASTER_PAGE_ID, keyframeEffectPointer.id)
            if (!dal.has(keyframeEffectListPointer)) {
                dataModel.addItem(
                    {
                        type: 'KeyframeEffectList',
                        values: []
                    },
                    EFFECTS,
                    MASTER_PAGE_ID,
                    'KEYFRAME_EFFECT_LIST'
                )
            }
            const keyframeEffectListDataItem = getKeyframeEffectList()
            dal.set(getInnerPointer(keyframeEffectListPointer, 'values'), [
                ...keyframeEffectListDataItem.values,
                createInnerRef(keyframeEffectPointer.id)
            ])
            return keyframeEffectPointer
        }

        const getKeyframeEffect = (pointer: Pointer): DalValue => {
            return dataModel.getItem(pointer.id, EFFECTS, MASTER_PAGE_ID)
        }

        const getAllEffectsPointers = (componentPointer: Pointer): Pointer[] => {
            const effectsListItem = dataModel.components.getItem(componentPointer, EFFECTS)
            return _.map(effectsListItem?.values, ({id}) => getPointer(getIdFromRef(id), EFFECTS))
        }

        const usesNewAnimations = (): boolean => {
            return Boolean(dal.get(pointers.data.getDataItemFromMaster(MASTER_PAGE_ID))?.usesNewAnimations)
        }

        const usesNewBgScrub = (): boolean => {
            return (
                usesNewAnimations() &&
                dataFixerVersioning.hasFixerRunOnCurrentVersion(
                    MASTER_PAGE_ID,
                    FixerCategory.VIEWER,
                    'migrateScreenInBehaviorsToEntranceEffects',
                    2,
                    {
                        fixers: {
                            migrateScreenInBehaviorsToEntranceEffects:
                                'MTA3X2Q3MjA3NmE5OGIzOGZlNDdkNTUyZjQwNzE1YjgxYzc2'
                        },
                        lastFixerIndex: -1
                    }
                )
            )
        }

        const updateKeyframeEffect = (
            keyframeEffectPointer: Pointer,
            newKeyframeEffectData: KeyframeEffectData
        ): void => {
            dataModel.addItem(newKeyframeEffectData, EFFECTS, MASTER_PAGE_ID, keyframeEffectPointer.id)
        }

        const removeKeyframeEffect = (keyframeEffectPointer: Pointer): void => {
            dataModel.removeItemRecursively(keyframeEffectPointer)
            const keyframeEffectListDataItem = getKeyframeEffectList()
            const keyframeEffectId = `#${keyframeEffectPointer.id}`
            const updatedKeyframeEffectList = _.reject(keyframeEffectListDataItem.values, id => id === keyframeEffectId)
            dal.set(getInnerPointer(keyframeEffectListPointer, ['values']), updatedKeyframeEffectList)
        }

        const createEffectsError = (message: string) => {
            return new ReportableError({
                message,
                errorType: 'EffectsError'
            })
        }

        return {
            effects: {
                usesNewAnimations,
                usesNewBgScrub,
                createEffectsError,
                list: getAllEffectsPointers
            },
            keyframeEffect: {
                add: addKeyframeEffect,
                get: getKeyframeEffect,
                list: getAllKeyframeEffectPointers,
                update: updateKeyframeEffect,
                remove: removeKeyframeEffect
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {keyframeEffect, effects} = extensionAPI as EffectsExtensionAPI
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        return {
            keyframeEffect: {
                add: keyframeEffect.add,
                get: keyframeEffect.get,
                list: keyframeEffect.list,
                update: keyframeEffect.update,
                remove: keyframeEffect.remove,
                getKeyframeEffectToAddRef: () => getPointer(dataModel.generateItemIdWithPrefix(EFFECTS), EFFECTS)
            },
            effects: {
                usesNewBgScrub: effects.usesNewBgScrub
            }
        }
    }

    return {
        name: 'effects',
        dependencies: new Set(['dataModel', 'dataFixerVersioning']),
        createExtensionAPI,
        createPublicAPI
    }
}

export interface EffectsExtensionAPI extends ExtensionAPI {
    effects: {
        usesNewAnimations(): boolean
        usesNewBgScrub(): boolean
        list(componentPointer: Pointer): Pointer[]
    }
    keyframeEffect: {
        list(): Pointer[]
        remove(pointer: Pointer): void
        get(pointer: Pointer): DalValue
        add(pointer: Pointer, keyframeEffectData: KeyframeEffectData): Pointer
        update(pointer: Pointer, keyframeEffectData: KeyframeEffectData): void
    }
}

export {createExtension}
